import config from "@/config";
import { TEST_NET_ID, TimePeriod } from "@/types/enum";

export const agoFromNow = (
  date: string,
  t?: (key: string, options?: any) => string
): string => {
  const now = new Date();
  const pastDate = new Date(date);
  const diffInMs = now.getTime() - pastDate.getTime();

  // Convert to appropriate time units
  const seconds = Math.floor(diffInMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  // If translation function is provided, use it
  if (t) {
    if (years > 0) {
      return `${years} ${t(years === 1 ? "common.time_ago.years.one" : "common.time_ago.years.other", { count: years })}`;
    } else if (months > 0) {
      return `${months} ${t(months === 1 ? "common.time_ago.months.one" : "common.time_ago.months.other", { count: months })}`;
    } else if (days > 0) {
      return `${days} ${t(days === 1 ? "common.time_ago.days.one" : "common.time_ago.days.other", { count: days })}`;
    } else if (hours > 0) {
      return `${hours} ${t(hours === 1 ? "common.time_ago.hours.one" : "common.time_ago.hours.other", { count: hours })}`;
    } else if (minutes > 0) {
      return `${minutes} ${t(minutes === 1 ? "common.time_ago.minutes.one" : "common.time_ago.minutes.other", { count: minutes })}`;
    } else {
      return seconds <= 1
        ? t("common.time_ago.just_now")
        : `${seconds} ${t(seconds === 1 ? "common.time_ago.seconds.one" : "common.time_ago.seconds.other", { count: seconds })}`;
    }
  }

  // Fallback to English if no translation function is provided
  if (years > 0) {
    return years === 1 ? "1 year ago" : `${years} years ago`;
  } else if (months > 0) {
    return months === 1 ? "1 month ago" : `${months} months ago`;
  } else if (days > 0) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  } else if (hours > 0) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  } else if (minutes > 0) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  } else {
    return seconds <= 1 ? "just now" : `${seconds} seconds ago`;
  }
};

export function sortByDate<T>(
  array: T[],
  dateField: keyof T,
  order: "asc" | "desc" = "desc"
): T[] {
  return [...array].sort((a, b) => {
    const dateA = new Date(a[dateField] as string).getTime();
    const dateB = new Date(b[dateField] as string).getTime();
    return order === "desc" ? dateB - dateA : dateA - dateB;
  });
}

export const formatDate = (
  date: Date | string,
  locale: string = "en-GB"
): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

export function formatNumberString(val: any) {
  if (val === undefined || val === null || isNaN(val)) return "0";
  const str = typeof val === "number" ? val.toString() : val;
  if (!str.includes(".")) return str; // No dot, return as is
  const [intPart, decimals = ""] = str.split(".");
  if (/^0+$/.test(decimals)) return intPart; // All zeros after dot
  if (decimals.length === 0) return str; // Has dot but no decimals
  if (decimals.length <= 2) {
    // If second decimal is zero, return only first decimal
    if (decimals.length === 2 && decimals.charAt(1) === "0") {
      return `${intPart}.${decimals.charAt(0)}`;
    }
    return str; // 1 or 2 decimals, return as is
  }
  // If first 2 decimals are both zeros, return only integer part
  if (decimals.charAt(0) === "0" && decimals.charAt(1) === "0") {
    return intPart;
  }
  // If second decimal is zero, return only first decimal
  if (decimals.charAt(1) === "0") {
    return `${intPart}.${decimals.charAt(0)}`;
  }
  // Otherwise trim to 2 decimals (no rounding)
  return `${intPart}.${decimals.slice(0, 2)}`;
}

export const utils = {
  normalizeDate: (date: Date): Date => {
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const normalized = new Date(
      date.toLocaleString("en-US", { timeZone: userTimezone })
    );
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  },

  computeDaysDifference: (date1: Date, date2: Date): number =>
    Math.floor((date1.getTime() - date2.getTime()) / (1000 * 60 * 60 * 24)),

  computeStreak: (
    today: Date,
    lastCheckIn: Date,
    checkInStreakValue: number
  ): number => {
    const daysDiff = utils.computeDaysDifference(today, lastCheckIn);
    return daysDiff > 1 ? 0 : checkInStreakValue;
  },
};

export function formatMoneyShort(num: number): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(1).replace(/\.0$/, "") + "B";
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(1).replace(/\.0$/, "") + "M";
  } else if (num >= 1_000) {
    return (num / 1_000).toFixed(1).replace(/\.0$/, "") + "K";
  } else {
    return num.toString();
  }
}

export function formatMoney(num: any): string {
  if (num >= 1_000_000_000) {
    return (num / 1_000_000_000).toFixed(2).replace(/\.00$/, "") + "B";
  } else if (num >= 1_000_000) {
    return (num / 1_000_000).toFixed(2).replace(/\.00$/, "") + "M";
  } else {
    // Format all numbers (including those less than 1,000) with commas
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
}

export const shortenAddress = (
  address: string,
  first: number = 4,
  last: number = 4
) => {
  if (!address) return "";
  return `${address.substring(0, first)}...${address.substring(address.length - last)}`;
};

export function sortRank<T extends Record<string, any>>(
  entries: T[],
  options?: {
    primaryField?: keyof T;
    secondaryField?: keyof T;
    order?: "asc" | "desc";
    includeRanking?: boolean;
  }
): T[] | Array<T & { rank: string; state: string }> {
  const {
    primaryField = "deposited" as keyof T,
    secondaryField = "mPoint" as keyof T,
    order = "desc",
    includeRanking = true,
  } = options || {};

  // Create a copy to avoid mutating the original array
  const sortedEntries = [...entries].sort((a, b) => {
    const valueA = Number(a[primaryField] || 0);
    const valueB = Number(b[primaryField] || 0);

    // Primary field comparison
    if (valueA !== valueB) {
      return order === "desc" ? valueB - valueA : valueA - valueB;
    }

    // Secondary field comparison if primary values are equal
    const secondaryA = Number(a[secondaryField] || 0);
    const secondaryB = Number(b[secondaryField] || 0);
    return order === "desc" ? secondaryB - secondaryA : secondaryA - secondaryB;
  });

  if (!includeRanking) {
    return sortedEntries;
  }

  // Add ranking information
  return sortedEntries.map((item, index) => ({
    ...item,
    rank: `#${index + 1}`,
    state: item.rankChange?.toString() || "0",
  }));
}

export function formatLabelChartTime(
  timeData: string[],
  timePeriod: TimePeriod,
  locale: string
) {
  if (timePeriod === TimePeriod.WEEKLY) {
    return timeData.map((time) => {
      const date = new Date(time);
      return date.toLocaleDateString(locale, {
        month: "short",
        day: "numeric",
      });
    });
  } else if (timePeriod === TimePeriod.MONTHLY) {
    return timeData.map((time) => {
      const date = new Date(time);
      return date.toLocaleDateString(locale, { month: "short" });
    });
  } else if (timePeriod === TimePeriod.YEARLY) {
    return timeData.map((time) => {
      const date = new Date(time);
      return date.toLocaleDateString(locale, { year: "numeric" });
    });
  }
  return timeData;
}

export function formatTokenSymbol(tokenSymbol: string): string {
  if (
    !tokenSymbol ||
    tokenSymbol.toUpperCase() === "UNKNOWN" ||
    tokenSymbol.toUpperCase() === "N/A"
  ) {
    return "USDC";
  }
  return tokenSymbol;
}

export function mappersNetwork(network: string): string {
  switch (network) {
    case "11155111":
      return "Sepolia";
    case "8453":
      return "Base";
    case "42161":
      return "Arbitrum";
    case "56":
      return "BNB Chain";
    case "137":
      return "Polygon";
    case "97":
      return "BNB Testnet";
    case "Mainnet Beta":
      return "Solana";
    default:
      return "Ethereum";
  }
}

export function mappersNetworkToId(network: string): string {
  switch (network) {
    case "Sepolia":
      return "11155111";
    case "Base":
      return "8453";
    case "Arbitrum":
      return "42161";
    case "BNB Chain":
      return "56";
    case "Polygon":
      return "137";
    default:
      return "8453";
  }
}
export function mapNetworkToIcon(chainId: string): string {
  switch (chainId) {
    case "11155111":
      return "sepolia";
    case "8453":
      return "base";
    case "42161":
      return "arbitrum";
    case "56":
      return "bnb-chain";
    case "137":
      return "polygon";
    case "97":
      return "bnb-testnet";
    case "1":
      return "ethereum";
    case "Mainnet Beta":
      return "solana";
    default:
      return "ethereum";
  }
}


export function mapperTokenOut(currency: string): string {
  if (currency === "Sepolia") {
    return "0xF3F2b4815A58152c9BE53250275e8211163268BA";
  }
  return "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";
}

export function mappersNetworkAddress(chain: string): string[] {
  const networkAddresses: Record<string, string[]> = {
    "8453": [
      "0xed91007e54875Ac5A0D22b85822e9864a1CE2589", // base router
      "0xc43AEEd6fBE99bf0AfbBFB0D0Fc1523A8c81E0f7", // base cross chain
      "0x0e1bb2a458db603ec57279126e6b1362433569c5", // base fund vault
      "0xA9A06D14E839C74001A04A47FCc4676B8FCb9FF0", // base controller
      "0x39a18374B0357572E23c17F3057a2B0D8ed684A5", // base referral
      "0xE7C33CB97428C258E9637b2449368F13a5B6bACC", // base stargate
      "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", // base usdc
      "0xf8a0bf9cf54bb92f17374d9e9a321e6a111a51bd", // base usdt
      "0x1e3209ca0eea83f59da55a9d15f9596800889116", // base lp usdc
    ],
    "42161": [
      "0x03afBc04c44d648DD59fC9CafB2B00730Bf42593", // arbitrum router
      "0xb17ED50d2D5C3CACaf5b81e89C15b95B7Ce2CfB5", // arbitrum cross chain
      "0x1a842A4F6C9FaDA6230581cAfBE6619D4B3aBA7d", // arbitrum fund vault
      "0x1d9aAfD68C9F8B5F6dEb25c8243e86C31ea9F102", // arbitrum controller
      "", // arbitrum referral
      "0x6243880bbc74a966A1C123c9c61b0f67d0bbb959", // arbitrum stargate
      "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", // arbitrum usdc
      "0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9", // arbitrum usdt
      "0xde20Cfa6B44767767703569CA70505Db51611d61", // arbitrum lp usdc
    ],
    "56": [
      config.BNB.ROUTER_LOCAL, // bnb router
      config.BNB.CROSS_CHAIN, // bnb cross chain
      config.BNB.FUND_VAULT, // bnb fund vault
      config.BNB.ADDRESS, // bnb controller
      "", // bnb referral
      config.BNB.STARGATE, // bnb stargate
      config.BNB.USDC, // bnb usdc
      config.BNB.USDT, // bnb usdt
      config.BNB.LP_USDC, // bnb lp usdc
    ]
  };

  // Default to Base chain addresses if chain ID is not found
  return networkAddresses[chain] || networkAddresses["8453"];
}

export const formatLabel = (label: string): string => {
  // Chain mappings
  if (label === TEST_NET_ID.BASE.toString()) {
    return "Base";
  }
  if (label === TEST_NET_ID.ARBITRUM.toString()) {
    return "Arbitrum";
  }
  if (label === TEST_NET_ID.BSC_MAIN.toString()) {
    return "BNB Smart Chain";
  }
  // Protocol mappings
  if (label === config.BASE.ALLBRIDGE.toLowerCase()) {
    return "Allbridge (Base)";
  }

  if (label === config.ARBITRUM.ALLBRIDGE.toLowerCase()) {
    return "Allbridge (Arbitrum)";
  }

  // if (label === config.BASE.BALANCER.toLowerCase()) {
  //   return "Balancer (Base)";
  // }

  // if (label === config.ARBITRUM.BALANCER.toLowerCase()) {
  //   return "Balancer (Arbitrum)";
  // }
  
  // Token mappings
  if (label === config.BASE.USDC.toLowerCase()) {
    return "USDC";
  }
  // For other addresses, use shortenAddress
  if (label.startsWith("0x")) {
    return shortenAddress(label);
  }

  return label;
};
